import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "../lib/supabase";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Checkbox } from "./ui/checkbox";

interface AgeVerifiedAuthProps {
    view: "sign_in" | "sign_up";
    title: string;
    description: string;
    redirectTo?: string;
}

export function AgeVerifiedAuth({ view, title, description, redirectTo }: AgeVerifiedAuthProps) {
    const [ageVerified, setAgeVerified] = useState(false);
    const [showAgeError, setShowAgeError] = useState(false);

    if (view === "sign_up" && !ageVerified) {
        return (
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <CardTitle>Age Verification Required</CardTitle>
                    <CardDescription>You must be 13 years or older to create an account</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="space-y-4">
                        <div className="flex items-start space-x-3">
                            <Checkbox
                                id="age-verification"
                                checked={ageVerified}
                                onCheckedChange={(checked: boolean) => {
                                    setAgeVerified(checked === true);
                                    if (checked) setShowAgeError(false);
                                }}
                                className="mt-1"
                            />
                            <label htmlFor="age-verification" className="text-sm leading-relaxed cursor-pointer">
                                I am 13 years of age or older
                            </label>
                        </div>

                        {showAgeError && (
                            <div className="p-4 bg-orange-50 border border-orange-200 rounded-md">
                                <p className="text-sm text-orange-800 font-medium mb-2">Account creation restricted</p>
                                <p className="text-sm text-orange-700">
                                    If you're under 13, please ask a parent or guardian to create an account and use
                                    StoryDoodle together. Parents can supervise and help with all the drawing and
                                    storytelling activities!
                                </p>
                            </div>
                        )}
                    </div>

                    <Button
                        onClick={() => {
                            if (!ageVerified) {
                                setShowAgeError(true);
                            }
                        }}
                        className="w-full"
                        disabled={!ageVerified}
                    >
                        Continue to Sign Up
                    </Button>

                    <div className="text-xs text-muted-foreground text-center space-y-2">
                        <p>
                            By creating an account, you agree to our{" "}
                            <Link to="/terms" className="underline hover:text-foreground">
                                Terms of Service
                            </Link>{" "}
                            and{" "}
                            <Link to="/privacy" className="underline hover:text-foreground">
                                Privacy Policy
                            </Link>
                            .
                        </p>
                        <p>Parents: You can delete all account data at any time from account settings.</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="w-full max-w-md">
            <CardHeader className="text-center">
                <CardTitle>{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
            </CardHeader>
            <CardContent>
                <style>
                    {`
                        /* Hide Supabase's sign up/sign in toggle links but keep forgot password */
                        .supabase-auth-ui_ui-anchor[href="#auth-sign-up"],
                        .supabase-auth-ui_ui-anchor[href="#auth-sign-in"] {
                            display: none !important;
                        }
                    `}
                </style>
                <Auth
                    supabaseClient={supabase}
                    view={view}
                    appearance={{
                        theme: ThemeSupa,
                        variables: {
                            default: {
                                colors: {
                                    brand: "hsl(var(--primary))",
                                    brandAccent: "hsl(var(--primary))",
                                    brandButtonText: "hsl(var(--primary-foreground))",
                                    defaultButtonBackground: "hsl(var(--secondary))",
                                    defaultButtonBackgroundHover: "hsl(var(--secondary))",
                                    inputBackground: "hsl(var(--background))",
                                    inputBorder: "hsl(var(--border))",
                                    inputBorderHover: "hsl(var(--border))",
                                    inputBorderFocus: "hsl(var(--ring))",
                                    inputText: "hsl(var(--foreground))",
                                    inputPlaceholder: "hsl(var(--muted-foreground))",
                                },
                            },
                        },
                        className: {
                            button: "w-full px-4 py-2 rounded-md font-medium transition-colors",
                            input: "w-full px-3 py-2 rounded-md border text-sm",
                            label: "text-sm font-medium",
                            message: "text-sm",
                            container: "space-y-4",
                            divider: "text-center text-sm text-muted-foreground",
                        },
                    }}
                    providers={["google"]}
                    redirectTo={redirectTo}
                    showLinks
                    magicLink={false}
                    onlyThirdPartyProviders={false}
                />
            </CardContent>
        </Card>
    );
}
